<script>
import { mapGetters } from 'vuex'
import { dateTimeFormatters } from '@src/utils/date-formatters'
const { formatDateUnix } = dateTimeFormatters()

export default {
  props: {
    subscription: {
      type: Object,
      required: true,
      default: () => {}
    },
    isSelected: {
      type: Boolean,
      required: true,
      default: true
    },
    noSub: {
      type: Boolean,
      required: false,
      default: false
    },
    hideSubscribedFlag: {
      type: Boolean,
      required: false,
      default: false
    },
    fromChallenge: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      currency_symbols: {
        USD: '$', // US Dollar
        EUR: '€', // Euro
        CRC: '₡', // Costa Rican Colón
        GBP: '£', // British Pound Sterling
        ILS: '₪', // Israeli New Sheqel
        INR: '₹', // Indian Rupee
        JPY: '¥', // Japanese Yen
        KRW: '₩', // South Korean Won
        NGN: '₦', // Nigerian Naira
        PHP: '₱', // Philippine Peso
        PLN: 'zł', // Polish Zloty
        PYG: '₲', // Paraguayan Guarani
        THB: '฿', // Thai Baht
        UAH: '₴', // Ukrainian Hryvnia
        VND: '₫' // Vietnamese Dong
      }
    }
  },
  mounted () {},
  computed: {
    ...mapGetters(['primaryColorRgb', 'activeSubscription']),
    perWeek () {
      const interval = this.subscription.interval
      const intervalCount = this.subscription.interval_count
      const amount = this.subscription.amount
      if (interval === 'year') {
        return (((amount / 100) / intervalCount) / 52).toFixed(2)
      }
      if (interval === 'month') {
        return (((amount / 100) / intervalCount) / 4).toFixed(2)
      }
      if (interval === 'week') {
        return ((amount / 100) / intervalCount).toFixed(2)
      }
      if (interval === 'day') {
        return (((amount / 100) / intervalCount) * 7).toFixed(2)
      } else {
        return 0
      }
    },
    priceInterval () {
      const interval = this.subscription.interval
      const intervalCount = this.subscription.interval_count
      if (interval === 'year') { return 'Annually' }
      if (interval === 'month') { if (intervalCount === 1) { return 'Monthly' } if (intervalCount === 3) { return 'Quarterly' } }
      if (interval === 'week') { return 'Weekly' }
      if (interval === 'day') { return 'Daily' } else {
        return ''
      }
    },
    currencySymbol () {
      const currency = this.subscription.currency.toUpperCase()
      return this.currency_symbols[currency]
    },
    isCurrent () {
      return this.activeSubscription && this.activeSubscription.plan.id === this.subscription.id
    },
    isPaused () {
      return this.activeSubscription.pause_collection && this.activeSubscription.pause_collection.behavior === 'keep_as_draft'
    },
    isAnnual () {
      return this.activeSubscription?.plan?.interval === 'year'
    },
    isCanceled () {
      return this.activeSubscription && this.activeSubscription.cancel_at_period_end
    },
    canceledDate () {
      return this.activeSubscription && this.activeSubscription.cancel_at_period_end ? formatDateUnix(this.activeSubscription.cancel_at, 'MMMM Do YYYY') : null
    },
    pausedDate () {
      return this.activeSubscription && this.activeSubscription.pause_collection ? formatDateUnix(this.activeSubscription.pause_collection.resumes_at, 'YYYY/MM/DD') : null
    },
    endDate () {
      return this.activeSubscription && this.activeSubscription.current_period_end ? formatDateUnix(this.activeSubscription.current_period_end, 'YYYY/MM/DD') : null
    }
  },
  methods: {}
}
</script>

<template>
  <b-row
    :class="['card-container', { isSelected: isSelected, fromChallenge: fromChallenge }]"
    :style="{ '--primaryColor': `rgba(${primaryColorRgb}, ${1})` }"
    @click="$emit('select-price', subscription)"
  >
   <!-- Enable if neeeded
   <div class="term">{{priceInterval}}</div> -->
    <b-col class="card price">
      <div class="row card-row">
        <div class="col-1 check-col">
          <div v-if="!noSub" class="check">
            <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="isSelected" v-bind:class="'icon-check'" v-bind:svg-inline="''" @click="$emit('select-price', subscription)" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M33 66c18.225 0 33-14.775 33-33S51.225 0 33 0 0 14.775 0 33s14.775 33 33 33z" fill="url(#paint0_linear)" fill-opacity=".6"/><path d="M17.368 33.55l4.466-5.321 8.645 7.255 15.748-18.116 5.243 4.56L35.803 39.95l-4.466 5.322L17.368 33.55z" fill="#fff"/><defs><linearGradient id="paint0_linear" x1="33" y1="0" x2="33" y2="66" gradientUnits="userSpaceOnUse"><stop stop-color="#55BCF5"/><stop offset="1" stop-color="#3E4FE2"/></linearGradient></defs></svg>
            <div
              v-if="!isSelected"
              class="icon-uncheck"
              @click="$emit('select-price', subscription)"
            />
          </div>
        </div>
        <div class="col">
          <div class="row nick-name">
            <p>{{subscription.interval === 'year' ? 'Annual' : priceInterval}} Subscription</p>
          </div>
          <div class="row duration">
            <span class="subscription-details">{{currencySymbol}}{{perWeek}}/wk, Billed {{ priceInterval }}; renews on {{ endDate }}</span> <span v-if="isCurrent && isSelected && isPaused"> - Paused until {{ pausedDate }}</span>
            <p v-if="isCurrent && isSelected && isCanceled" class="canceled"> - Canceled. <span class="canceledDate">You will have access until {{canceledDate}}</span></p>
          </div>
        </div>
        <div v-if="!isCurrent || hideSubscribedFlag" class="col total-price">
          <p>{{currencySymbol}}{{subscription.amount / 100 }}</p>
        </div>
        <div v-if="isCurrent && !hideSubscribedFlag" class="col already-subscribed">
          <p>Already Subscribed</p>
        </div>
      </div>
      <div class="annual-callout" v-if="!isAnnual">
        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.664 23.691c6.41 0 11.684-5.273 11.684-11.683S19.074.324 12.664.324C6.254.324.98 5.598.98 12.008c0 6.41 5.274 11.683 11.684 11.683zm0-2.859a8.807 8.807 0 01-8.824-8.824 8.802 8.802 0 018.824-8.836 8.827 8.827 0 018.836 8.836 8.81 8.81 0 01-8.836 8.824zm0-7.137c.703 0 1.125-.398 1.149-1.136l.175-4.63c.035-.773-.527-1.3-1.336-1.3-.82 0-1.359.527-1.336 1.289l.176 4.652c.035.727.445 1.125 1.172 1.125zm0 3.785c.82 0 1.453-.527 1.453-1.324 0-.785-.633-1.312-1.453-1.312-.82 0-1.465.527-1.465 1.312s.645 1.325 1.465 1.325z" fill="#6EBEB5"/></svg>
        <span>Looking for the best deal? Upgrade to Annual</span>
      </div>
    </b-col>
  </b-row>
</template>

<style lang="scss" scoped>
@import '@design';

.card-container {
  position: relative;
  width: 100%;
   margin-left: unset;
  .term {
    position: absolute;
    z-index: 2;
    border-top-left-radius: 8px;
    background-color: var(--primaryColor);
    color: white;
    top: 0px;
    padding: 0rem 1.2rem;
    text-transform: capitalize;
  }
  &:not(.isSelected) {
    transition: transform 300ms;
    transition-timing-function: ease-out;
    &:hover {
      transform: scale(1.01);
    }
    cursor: pointer;
  }
  &.isSelected {
    &.fromChallenge {
      .price {
       border: 2px solid var(--primaryColor);
     }
    }
  }
  .price {
    position: relative;
    margin-bottom: 0.5rem;
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
    border-radius: 8px;
    width: 100%;
    .card-row {
      flex-wrap: unset;
    }
    .col-2 {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex: 0 0 12.666667%;
      max-width: 12.666667%;
    }
    p {
      font-size: 1.5rem;
      font-weight: 900;
      margin-bottom: unset;
    }
    .duration {
      p {
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        line-height: 19px;
        align-items: center;
        color: #000000;
      }
      .canceled {
        color: red;
      }
      .canceledDate {
        font-size: 12px;
      }
    }
    .nick-name {
      p {
        white-space: nowrap;
        font-style: normal;
        font-weight: bold;
        font-size: 19px;
        line-height: 26px;
        align-items: center;
        color: #000000;
        text-align: start;
      }
    }
    .col-1 {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      flex: 0 0 10.333333%;
      max-width: fit-content;
      &.check-col {
        margin-right: 1rem;
        max-width: 3.5rem;
        min-width: 3.5rem;
        .check {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          .icon-check {
            width: 1.5rem;
            height: 1.5rem;
            path {
              &:first-child {
                fill: var(--primaryColor);
                fill-opacity: 1;
              }
            }
          }
          .icon-uncheck {
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 50%;
            border: 1px black solid;
            cursor: pointer;
          }
        }
      }
      p {
        font-style: normal;
        font-weight: 600;
        font-size: 25px;
        line-height: 30px;
        display: flex;
        align-items: center;
        text-align: center;
        color: rgba(0, 0, 0, 0.85);
      }
    }
    .total-price {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-right: 2rem;
      p {
        white-space: nowrap;
        font-style: normal;
        font-weight: 600;
        font-size: 25px;
        line-height: 30px;
        display: flex;
        align-items: center;
        text-align: right;
        color: rgba(0, 0, 0, 0.85);
      }
    }
    .already-subscribed {
       display: flex;
      justify-content: flex-end;
      align-items: center;
      p {
        font-style: normal;
        font-weight: 600;
        font-size: 16px;
        line-height: 18px;
        color: rgba(0, 0, 0, 0.85);
      }
    }

    .annual-callout {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 20px;
      span {
        font-family: $base-font-family;
        font-style: normal;
        font-weight: bold;
        font-size: 14px;
        color: #6EBEB5;
        margin-left: 10px;
      }
    }
  }
  @media screen and (max-width: 600px) {
    width: 100%;
    margin-right: unset;
    margin-left: unset;
    .price {
      .card-row {
        .check-col {
          margin-right: unset !important;
        }
        .col {
          .nick-name {
            p {
              font-size: 16px;
            }
          }
          .duration {
            p {
              font-size: 14px;
              white-space: nowrap;
            }
          }
        }
        .already-subscribed {
          text-align: center;
        }
        .total-price {
          margin-right: unset;
          p { font-size: 20px; }
        }
      }
    }
  }
  // &.disabled {
  //    pointer-events: none;
  //    .price {
  //      background-color: rgba(black, 0.15);
  //      opacity: 0.5;
  //    }
  //  }
}
</style>
